import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';
import ProductServiceModal from 'components/ProductsServices/ProductServiceModal';
import { currencyFormattedAmount } from 'helpers/currency';

// Layout shared by the list and its loading skeleton (pages/products_services/index.js).
export const PRICEBOOK_COLUMNS = [
  { area: 'name', label: 'Item', width: 'minmax(220px, 2fr)', bone: 'lines' },
  { area: 'type', label: 'Type', width: 'minmax(90px, 0.6fr)', bone: 'pill' },
  { area: 'category', label: 'Category', width: 'minmax(130px, 1fr)', bone: 'short', icon: 'folder-close' },
  { area: 'part', label: 'Part number', width: 'minmax(110px, 0.8fr)', bone: 'short', icon: 'barcode' },
  { area: 'price', label: 'Price', width: 'minmax(100px, 0.7fr)', bone: 'short', icon: 'dollar' },
  { area: 'markup', label: 'Markup', width: 'minmax(80px, 0.5fr)', bone: 'short', icon: 'trending-up' },
  { area: 'quickbooks', label: 'QuickBooks', width: 'minmax(120px, 0.8fr)', bone: 'pill', icon: 'link' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const PRICEBOOK_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['name actions', 'type type', 'category category', 'part part', 'markup markup'],
  footer: ['price', 'quickbooks'],
};

export const PRICEBOOK_MIN_TABLE_WIDTH = 980;

// Services are labor; everything else is a material.
const isLabor = (attributes) => attributes.product_service_type === 'Service' || attributes.product_service_type === 'Services';

const isLinked = (attributes) => Array.isArray(attributes.external_mappings) && attributes.external_mappings.length > 0;

const blank = (value) => value === null || value === undefined || value === '';

// The pricebook list (Settings > Pricebook): each item with its type,
// category, part number, price, markup and QuickBooks link. Read-only items
// (e.g. kept in step with the accounting app) can't be edited or deleted here.
@inject('productServiceStore', 'viewStore') @observer
class ProductsServicesTable extends Component {
  @observable deleting = null; // the item waiting for delete confirmation
  @observable isProductServiceDialogVisible = false;
  @observable itemType = 'Labor';

  openEdit(productService) {
    this.props.productServiceStore.productService.object = productService.attributes;
    this.itemType = isLabor(productService.attributes) ? 'Labor' : 'Materials';
    this.isProductServiceDialogVisible = true;
  }

  confirmDelete = () => {
    const item = this.deleting;
    this.deleting = null;
    this.props.productServiceStore.delete(item)
      .then(() => {
        Toast.show({ message: `${item.attributes.name} deleted.`, intent: Intent.SUCCESS });
      })
      .catch(() => {
        Toast.show({ message: `Couldn't delete ${item.attributes.name}. Try again.`, intent: Intent.DANGER });
      });
  };

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.productServiceIndexSearch = { ...viewStore.productServiceIndexSearch, page: newPage };
    this.props.productServiceStore.loadProductsServices();
  }

  // Empty list's "Clear search": back to every item, page 1.
  clearSearch = () => {
    const { viewStore } = this.props;
    viewStore.productServiceIndexSearch = { ...viewStore.productServiceIndexSearch, term: '*', page: 1 };
    if (viewStore.bumpListFilters) viewStore.bumpListFilters();
    this.props.productServiceStore.loadProductsServices();
  };

  render() {
    const { objects, meta = {} } = this.props.productServiceStore.productsServices;
    const { productServiceIndexSearch } = this.props.viewStore;
    const term = productServiceIndexSearch.term && productServiceIndexSearch.term !== '*' ? productServiceIndexSearch.term : '';
    const { onAdd } = this.props;
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="items"
          term={term}
          onClearFilters={this.clearSearch}
          title={term ? undefined : 'Your pricebook is empty'}
          message={term ? undefined : 'Add the labor and materials you charge for, so you can add them to estimates, work orders and invoices.'}
          extraAction={!term && onAdd && { label: 'Add item', icon: 'add', onClick: onAdd, primary: true }}
        />
      );
    }
    const deleting = this.deleting && this.deleting.attributes;
    return (
      <React.Fragment>
        <DataList.List
          columns={PRICEBOOK_COLUMNS}
          cards={PRICEBOOK_CARDS}
          minTableWidth={PRICEBOOK_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="items"
        >
          {objects.map((productService) => {
            const attributes = productService.attributes;
            return (
              <DataList.Row key={productService.id}>
                <DataList.Cell area="name">
                  <DataList.Lines primary={attributes.name} secondary={attributes.description} />
                </DataList.Cell>
                <DataList.Cell area="type">
                  {isLabor(attributes)
                    ? <DataList.Pill tone="purple">Labor</DataList.Pill>
                    : <DataList.Pill tone="blue">Material</DataList.Pill>}
                </DataList.Cell>
                <DataList.Cell area="category">
                  {attributes.product_category && attributes.product_category.title
                    ? attributes.product_category.title
                    : <DataList.Muted>–</DataList.Muted>}
                </DataList.Cell>
                <DataList.Cell area="part">
                  {blank(attributes.part_number) ? <DataList.Muted>–</DataList.Muted> : attributes.part_number}
                </DataList.Cell>
                <DataList.Cell area="price">
                  <span className="sp-number">
                    {blank(attributes.unit_price) ? '–' : currencyFormattedAmount(attributes.unit_price)}
                  </span>
                </DataList.Cell>
                <DataList.Cell area="markup">
                  {blank(attributes.mark_up) ? <DataList.Muted>–</DataList.Muted> : attributes.mark_up}
                </DataList.Cell>
                <DataList.Cell area="quickbooks">
                  {isLinked(attributes)
                    ? <DataList.Pill tone="green" icon="tick">Linked</DataList.Pill>
                    : <DataList.Muted>Not linked</DataList.Muted>}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  {attributes.is_readonly ? (
                    <span className="sl-readonly" title="This item can't be changed here">
                      <DataList.Muted>Read only</DataList.Muted>
                    </span>
                  ) : (
                    <DataList.Actions
                      items={[
                        { icon: 'edit', label: `Edit ${attributes.name}`, onClick: () => this.openEdit(productService) },
                        { icon: 'trash', label: `Delete ${attributes.name}`, intent: Intent.DANGER, onClick: () => { this.deleting = productService; } },
                      ]}
                    />
                  )}
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {meta.total_pages > 1 && (
          <Footer>
            <div className="bp3-navbar-group bp3-align-left">
              <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
            </div>
          </Footer>
        )}
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete item"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>{deleting ? `Delete ${deleting.name}?` : 'Delete this item?'}</strong>
          <p>It won't be offered when adding lines to estimates, work orders and invoices.</p>
        </Alert>
        <ProductServiceModal
          isOpen={this.isProductServiceDialogVisible}
          title="Update Pricebook"
          edit={true}
          onClose={() => { this.isProductServiceDialogVisible = false; }}
          onSuccess={() => { this.isProductServiceDialogVisible = false; }}
          itemType={this.itemType}
        />
      </React.Fragment>
    );
  }
}

export default ProductsServicesTable;
